import { DeepLinkType } from "pages/api/deep-links/types";
import {
  Dispatch,
  SetStateAction,
  useCallback,
  useMemo,
  useRef,
  useState,
} from "react";
import { useDeepLinks } from "src/hooks/useDeepLinks";
import { SearchActions } from "src/search/types";
import { SearchContext } from "src/search/useSearchContext";
import { createId } from "src/utils/strings";
import {
  ContentView,
  ContentViews,
  isContentView,
  SerializableView,
} from "./useContentView";
import { Tabs } from "./useTabs";

export type ExploreDocument = {
  // used so that unsaved things can have different ids
  id: string;
  // saved question
  title?: string;
  // this is actually the id of the deep link
  saved: false | string;
  setTitle: Dispatch<SetStateAction<string | undefined>>;
  setSaved: Dispatch<SetStateAction<string | false>>;
  // These two are for restoring session activity
  setId: Dispatch<SetStateAction<string>>;
  setInitial: Dispatch<SetStateAction<string>>;
  // this is derived
  status: "new" | "modified" | "saved";
  initial: string;
  open(args: {
    id?: string;
    title: string;
    serialized: string;
    saved: false | string;
    viz?: SerializableView;
  }): void;
};

// initial on load
export function useExploreDocument(
  loadInitial: SerializableDoc | undefined,
  current: string,
  context: { tabs: Tabs; view: ContentView; params: SearchContext["params"] }
): ExploreDocument {
  const controller = useRef(context);
  controller.current = context;
  const { trackHit } = useDeepLinks(DeepLinkType.EXPLORE);
  const [id, setId] = useState<string>(
    // If it is saved, we'll obviously use the ID provided.
    // If it is not saved, we need an ID.
    // Let's see if we are opening a tab, and use that one.
    // Otherwise we'll make one up.
    // This will eventually not work, if we ever need to batch-open tabs.
    loadInitial?.saved || `unsaved-${createId()}`
  );
  const [title, setTitle] = useState<string | undefined>(loadInitial?.title);
  const [saved, setSaved] = useState<false | string>(
    loadInitial?.saved || false
  );
  const [initial, setInitial] = useState(current); // bad name choice, initial on open
  const open = useCallback(
    (args: Parameters<ExploreDocument["open"]>[0]) => {
      const newId = args.saved
        ? `${args.saved}-${createId()}`
        : `unsaved-${createId()}`;
      setId(args.id || newId);
      setTitle(args.title);
      setInitial(args.serialized);
      setSaved(args.saved);
      if (args.saved) trackHit.mutate({ id: args.saved });
      controller.current.params.dispatch({
        type: SearchActions.loadState,
        value: args.serialized,
      });
      if (args.viz && isContentView(args.viz.value as unknown)) {
        controller.current.view.set(args.viz.value);
        controller.current.view.setConfig(args.viz.config);
      } else {
        controller.current.view.set(ContentViews.default);
        controller.current.view.setConfig({});
      }
    },
    [trackHit]
  );
  const status = useMemo(() => {
    if (id.startsWith("unsaved-")) return "new" as const;
    return initial === current
      ? saved
        ? ("saved" as const)
        : ("new" as const)
      : ("modified" as const);
  }, [current, initial, id, saved]);
  return {
    id,
    setId,
    initial,
    setInitial,
    open,
    saved,
    setTitle,
    setSaved,
    status,
    title,
  };
}

export type SerializableDoc = Pick<ExploreDocument, "title" | "saved">;

export function serializeDocument(d?: Partial<SerializableDoc>) {
  return JSON.stringify({ saved: d?.saved || false, title: d?.title });
}

export function unserializeDocument(text: string): SerializableDoc {
  const doc: SerializableDoc = {
    saved: false,
    title: undefined,
  };
  try {
    const obj = JSON.parse(text);
    doc.saved = obj.saved || doc.saved;
    doc.title = obj.title || doc.title;
  } catch {}
  return doc;
}

export function toSessionStorageDoc(doc: ExploreDocument) {
  const { id, saved, title, initial } = doc;
  return { id, saved, title, initial };
}
